// frontend-react/src/components/ProbablePitchers/ProbablePitchers.js
// Probable starting pitchers with roster intelligence and team filtering

import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { fetchProbablePitchers } from '../../services/probablePitchersApi';
import TeamPageHeader from '../common/TeamPageHeader';
import PlayerLink from '../PlayerLink';
import './ProbablePitchers.css';

/**
 * ProbablePitchers Component
 * Displays upcoming probable starting pitchers with roster ownership
 * 
 * @param {string} leagueId - League UUID
 * @param {string} teamId - User's team UUID
 * @param {object} league - League object with team info
 */
const ProbablePitchers = ({ leagueId, teamId, league, userTeam }) => {
  // State management
  const [startDate, setStartDate] = useState(getTodayDate());
  const [days, setDays] = useState(5);
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('my-team'); // 'my-team', 'free-agents', or team_id

  // Load data on mount and when parameters change
  useEffect(() => {
    if (leagueId && teamId) {
      loadProbablePitchers();
    }
  }, [leagueId, teamId, startDate, days]);

  /**
   * Get today's date in YYYY-MM-DD format
   */
  function getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Add days to a date string
   */
  function addDays(dateStr, daysToAdd) {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString().split('T')[0];
  }

  /**
   * Format date for display
   */
  function formatDisplayDate(dateStr) {
    const date = new Date(dateStr + 'T12:00:00'); // Noon to avoid timezone issues
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  /**
   * Load probable pitchers from API
   */
  const loadProbablePitchers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchProbablePitchers(leagueId, teamId, startDate, days);

      if (response.success) {
        setGames(response.games || []);
        setTeams(response.teams || []);
      } else {
        setError(response.detail || 'Failed to load probable pitchers');
      }
    } catch (err) {
      console.error('Error loading probable pitchers:', err);
      setError(err.message || 'Failed to load probable pitchers');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Navigate to previous date range
   */
  const handlePrevious = () => {
    setStartDate(addDays(startDate, -days));
  };

  /**
   * Navigate to next date range
   */
  const handleNext = () => {
    setStartDate(addDays(startDate, days));
  };

  /**
   * Go back to today
   */
  const handleToday = () => {
    setStartDate(getTodayDate());
  };

  /**
   * Format innings pitched from decimal (20.333) to baseball notation (20.1)
   */
  const formatIP = (ip) => {
    if (ip == null) return null;
    const full = Math.floor(ip);
    const frac = Math.round((ip - full) * 3);
    return `${full}.${frac}`;
  };

  /**
   * Build stat line for a pitcher: "2-1 · 3.24 ERA · 1.08 WHIP · 24.1 IP · 31 K"
   * Returns null if pitcher has no stats (TBD, rookie, etc.)
   */
  const buildStatLine = (pitcher) => {
    if (!pitcher || pitcher.era == null) return null;
    const parts = [];
    if (pitcher.wins != null && pitcher.losses != null) parts.push(`${pitcher.wins}-${pitcher.losses}`);
    if (pitcher.era != null) parts.push(`${pitcher.era.toFixed(2)} ERA`);
    if (pitcher.whip != null) parts.push(`${pitcher.whip.toFixed(2)} WHIP`);
    if (pitcher.innings_pitched != null) parts.push(`${formatIP(pitcher.innings_pitched)} IP`);
    if (pitcher.strikeouts_pitched != null) parts.push(`${pitcher.strikeouts_pitched} K`);
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  /**
   * Check if a pitcher should be highlighted based on current filter
   */
  const shouldHighlight = (pitcher) => {
    if (!pitcher) return false;

    if (selectedFilter === 'my-team') {
      return pitcher.team_id === teamId;
    } else if (selectedFilter === 'free-agents') {
      return pitcher.is_free_agent;
    } else {
      // Specific team selected
      return pitcher.team_id === selectedFilter;
    }
  };

  /**
   * Group games by date
   */
  const groupGamesByDate = () => {
    const grouped = {};
    games.forEach(game => {
      if (!grouped[game.game_date]) {
        grouped[game.game_date] = [];
      }
      grouped[game.game_date].push(game);
    });
    return grouped;
  };

  // Group games
  const gamesByDate = groupGamesByDate();
  const dates = Object.keys(gamesByDate).sort();

  // Render loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <RefreshCw className="spinner mb-4" size={40} color={dynastyTheme.tokens.colors.primary} />
          <p className={dynastyTheme.classes.text.neutralLight}>Loading probable pitchers...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <AlertCircle size={40} color={dynastyTheme.tokens.colors.error} className="mb-4" />
          <p className={`${dynastyTheme.classes.text.error} text-lg mb-4`}>{error}</p>
          <button 
            onClick={loadProbablePitchers}
            className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TeamPageHeader
        title="Probable Pitchers"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        badges={false}
        actions={
          <div className="flex items-center gap-3">
            {/* Date navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={handlePrevious}
                className={`p-2 ${dynastyTheme.components.button.ghost} ${dynastyTheme.classes.transition}`}
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleToday}
                className={`flex items-center gap-2 px-3 py-2 ${dynastyTheme.components.button.ghost} ${dynastyTheme.classes.transition}`}
              >
                <Calendar size={16} />
                <span>Today</span>
              </button>
              <button
                onClick={handleNext}
                className={`p-2 ${dynastyTheme.components.button.ghost} ${dynastyTheme.classes.transition}`}
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Team filter dropdown */}
            <div className="flex items-center gap-2">
              <label
                htmlFor="team-filter"
                className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight}`}
              >
                Highlight:
              </label>
              <select
                id="team-filter"
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className={`${dynastyTheme.components.input} px-3 py-1.5 min-w-[160px]`}
              >
                <option value="my-team">My Team</option>
                <option value="free-agents">Free Agents</option>
                <optgroup label="Other Teams">
                  {teams
                    .filter(t => t.team_id !== teamId)
                    .map(t => (
                      <option key={t.team_id} value={t.team_id}>
                        {t.team_name}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          </div>
        }
      />

      {/* Games display */}
      {dates.length === 0 ? (
        <div className="probable-pitchers-container flex flex-col items-center justify-center min-h-[300px]">
          <p className={`${dynastyTheme.classes.text.neutralLight} text-lg`}>
            No games scheduled for this date range.
          </p>
        </div>
      ) : (
        <div className="probable-pitchers-container space-y-10">
          {dates.map(date => (
            <div key={date}>
              <h3 className={`text-xl font-semibold ${dynastyTheme.classes.text.white} mb-4 pb-2 border-b ${dynastyTheme.classes.border.light}`}>
                {formatDisplayDate(date)}
              </h3>
              
              <div className="games-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gamesByDate[date].map(game => (
                  <div 
                    key={game.game_id}
                    className={`${dynastyTheme.components.card.base} p-4 ${dynastyTheme.classes.transition}`}
                  >
                    {/* Game info */}
                    <div className="mb-4 pb-3 border-b border-neutral-700/30">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className={`text-base font-semibold ${dynastyTheme.classes.text.white}`}>
                          {game.away_team}
                        </span>
                        <span className={dynastyTheme.classes.text.neutralLight}>@</span>
                        <span className={`text-base font-semibold ${dynastyTheme.classes.text.white}`}>
                          {game.home_team}
                        </span>
                      </div>
                      <div className={`text-center text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                        {game.game_time}
                      </div>
                    </div>

                    {/* Pitchers */}
                    <div className="space-y-3">
                      {/* Away pitcher */}
                      {(() => {
                        const isTBD = !game.away_pitcher.mlb_id || game.away_pitcher.name === 'TBD';
                        const statLine = buildStatLine(game.away_pitcher);
                        return (
                          <div
                            className={`p-3 rounded-md ${dynastyTheme.classes.bg.darkLighter} ${
                              shouldHighlight(game.away_pitcher)
                                ? `border-2 ${dynastyTheme.classes.border.success} bg-emerald-500/10`
                                : 'border border-transparent'
                            } ${dynastyTheme.classes.transition} relative`}
                            style={isTBD ? { opacity: 0.45 } : undefined}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-xs font-semibold ${dynastyTheme.classes.text.neutralLight} uppercase`}>
                                {game.away_team}
                              </span>
                              {game.away_pitcher.team_name && (
                                <span className={`text-xs ${dynastyTheme.classes.text.primary}`}>
                                  ({game.away_pitcher.team_name})
                                </span>
                              )}
                            </div>
                            <PlayerLink
                              mlbId={game.away_pitcher.mlb_id}
                              name={game.away_pitcher.name}
                              style={{ fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.25rem', display: 'block' }}
                            />
                            {statLine && (
                              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                                {statLine}
                              </div>
                            )}
                            {game.away_pitcher.is_free_agent && !isTBD && (
                              <div className={`absolute top-2 right-2 ${dynastyTheme.components.badge.info} text-[10px] px-2 py-0.5`}>
                                FA
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Home pitcher */}
                      {(() => {
                        const isTBD = !game.home_pitcher.mlb_id || game.home_pitcher.name === 'TBD';
                        const statLine = buildStatLine(game.home_pitcher);
                        return (
                          <div
                            className={`p-3 rounded-md ${dynastyTheme.classes.bg.darkLighter} ${
                              shouldHighlight(game.home_pitcher)
                                ? `border-2 ${dynastyTheme.classes.border.success} bg-emerald-500/10`
                                : 'border border-transparent'
                            } ${dynastyTheme.classes.transition} relative`}
                            style={isTBD ? { opacity: 0.45 } : undefined}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-xs font-semibold ${dynastyTheme.classes.text.neutralLight} uppercase`}>
                                {game.home_team}
                              </span>
                              {game.home_pitcher.team_name && (
                                <span className={`text-xs ${dynastyTheme.classes.text.primary}`}>
                                  ({game.home_pitcher.team_name})
                                </span>
                              )}
                            </div>
                            <PlayerLink
                              mlbId={game.home_pitcher.mlb_id}
                              name={game.home_pitcher.name}
                              style={{ fontSize: '0.875rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.25rem', display: 'block' }}
                            />
                            {statLine && (
                              <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                                {statLine}
                              </div>
                            )}
                            {game.home_pitcher.is_free_agent && !isTBD && (
                              <div className={`absolute top-2 right-2 ${dynastyTheme.components.badge.info} text-[10px] px-2 py-0.5`}>
                                FA
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Refresh button */}
      <div className="mt-8 text-center">
        <button 
          onClick={loadProbablePitchers}
          className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} inline-flex items-center gap-2`}
        >
          <RefreshCw size={16} />
          <span>Refresh</span>
        </button>
      </div>
    </div>
  );
};

export default ProbablePitchers;
